import { resolveItemsForBatch } from '../../operations/getItems';
import { IItemCacheThunkAction, IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { IPatch } from '../../utilities/Graft';
import { IFaceted } from '../../models/Facet';
import { mapKeys } from '../../store/reducers/keyMapReducer';
import { IInvalidateItemsPayload, invalidateItemsForBatch } from '../../operations/invalidateItems';
import { processBatch } from '../../store/reducers/itemCacheReducer';
import { processBatchWithEffects } from '../../operations/processBatchWithEffects';

export interface IResolveItemsPayload {
  items: {
    [itemKey: string]: IPatch<IFaceted>;
  };
}

export interface IItemResolver {
  mapKeys(payload: typeof mapKeys.payloadType): void;
  invalidateItems(payload: IInvalidateItemsPayload): void;
  resolveItems(payload: IResolveItemsPayload): void;
}

/**
 * Helper component which assists in dispatching resolveItems and mapKeys.
 * Create an instance of `ItemResolver` and invoke `resolveItems` repeatedly
 * while parsing results from a server response.
 * Item updates are carefully merged and do not accidentally override each other.
 *
 * Once done, dispatch a call to `itemResolver.execute()` to the store.
 *
 * ```ts
 * const itemResolver = new ItemResolver();
 * itemResolver.resolveItems({
 *  items: {
 *    [key1]: {
 *      ...fooFacet.pack({
 *        test1: 'a'
 *      })
 *    }
 *  }
 * });
 * itemResolver.resolveItems({
 *  items: {
 *    [key1]: { // Will not conflict.
 *      ...fooFacet.pack({
 *        test2: 'b'
 *      })
 *    }
 *  }
 * });
 * dispatch(itemResolver.execute());
 * ```
 */
export class ItemResolver {
  private _invalidateItems: IInvalidateItemsPayload[];
  private _resolveItems: IResolveItemsPayload[];
  private _mapKeys: (typeof mapKeys.payloadType)[];

  private _done?: boolean;

  constructor() {
    this._invalidateItems = [];
    this._resolveItems = [];
    this._mapKeys = [];
  }

  /**
   * Declares mapping between keys. These mappings will be pushed to the cache before items are invalidated or resolved.
   */
  public mapKeys(payload: typeof mapKeys.payloadType): void {
    if (this._done) {
      throw new Error('This ItemResolver has been merged or executed, and may no longer be modified.');
    }

    this._mapKeys.push(payload);
  }

  /**
   * Marks items to be invalidated. All items will be invalidated before any items are resolved.
   */
  public invalidateItems(payload: IInvalidateItemsPayload): void {
    if (this._done) {
      throw new Error('This ItemResolver has been merged or executed, and may no longer be modified.');
    }

    this._invalidateItems.push(payload);
  }

  /**
   * Provides resolutions for items. All resolutions will be put into the store in the correct order,
   * without conflict. Later resolutuons will take priority only if the same facet is resolved on the same items.
   */
  public resolveItems(payload: IResolveItemsPayload): void {
    if (this._done) {
      throw new Error('This ItemResolver has been merged or executed, and may no longer be modified.');
    }

    this._resolveItems.push(payload);
  }

  /**
   * Merges pending resolutions from a 'child' resolver.
   * All operations in the child will occur after those presently staged in the parent.
   * New operations added to the parent will occur after those in the child.
   */
  public merge(itemResolver: ItemResolver): void {
    itemResolver._done = true;

    this._mapKeys.push(...itemResolver._mapKeys);
    this._invalidateItems.push(...itemResolver._invalidateItems);
    this._resolveItems.push(...itemResolver._resolveItems);
  }

  /**
   * Creates an action which, when dispatched, executes the tasks in this ItemResolver in the correct sequence.
   */
  public execute(): IItemCacheThunkAction<void> {
    this._done = true;

    return (dispatch: IItemCacheDispatch) => {
      // Apply all key mappings first.
      dispatch(processBatch({
        actions: this._mapKeys.map((payload: (typeof mapKeys)['payloadType']) => {
          return mapKeys(payload);
        })
      }));

      dispatch(processBatchWithEffects({
        actionsWithEffects: this._invalidateItems.map(invalidateItemsForBatch)
      }));

      dispatch(processBatchWithEffects({
        actionsWithEffects: this._resolveItems.map(resolveItemsForBatch)
      }));
    };
  }
}
